import { useState, useEffect, useRef } from 'react';
import { getTenants, adminGetMessages, adminSendMessage, adminUnreadCount } from '../services/api';
import { ChatCircleDots, PaperPlaneTilt, User } from '@phosphor-icons/react';

export default function AdminMessages() {
  const [tenants, setTenants] = useState([]);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [messages, setMessages] = useState([]);
  const [content, setContent] = useState('');
  const [sending, setSending] = useState(false);
  const [unread, setUnread] = useState({});
  const scrollRef = useRef(null);

  const loadTenants = async () => {
    const res = await getTenants();
    setTenants(res.data.filter(t => t.has_portal_access));
    const uc = await adminUnreadCount();
    setUnread(uc.data);
  };

  const loadMessages = async (tenantId) => {
    if (!tenantId) return;
    const res = await adminGetMessages(tenantId);
    setMessages(res.data);
    const uc = await adminUnreadCount();
    setUnread(uc.data);
  };

  useEffect(() => {
    loadTenants();
  }, []);

  useEffect(() => {
    if (selectedTenant) {
      loadMessages(selectedTenant.id);
      const interval = setInterval(() => loadMessages(selectedTenant.id), 10000);
      return () => clearInterval(interval);
    }
  }, [selectedTenant]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages]);

  const handleSend = async (e) => {
    e.preventDefault();
    if (!content.trim() || !selectedTenant) return;
    setSending(true);
    try {
      await adminSendMessage(selectedTenant.id, content.trim());
      setContent('');
      await loadMessages(selectedTenant.id);
    } catch (err) {
      console.error(err);
    } finally {
      setSending(false);
    }
  };

  const formatTime = (iso) =>
    new Date(iso).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });

  return (
    <div className="space-y-6" data-testid="admin-messages-page">
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-black tracking-tight text-slate-900">
          Nachrichten
        </h1>
        <p className="text-slate-500 mt-1 text-sm sm:text-base">Kommunikation mit Ihren Mietern</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 h-[calc(100vh-12rem)]">
        {/* Tenant List */}
        <div className="bg-white border border-gray-200 flex flex-col">
          <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
            <h2 className="font-bold text-slate-900">Mieter ({tenants.length})</h2>
          </div>
          <div className="flex-1 overflow-y-auto">
            {tenants.length === 0 ? (
              <p className="text-sm text-slate-500 p-4">Keine Mieter mit Portal-Zugang.</p>
            ) : (
              tenants.map(t => (
                <button
                  key={t.id}
                  onClick={() => setSelectedTenant(t)}
                  className={`w-full text-left px-4 py-3 border-b border-gray-100 hover:bg-slate-50 flex items-center justify-between ${selectedTenant?.id === t.id ? 'bg-slate-100' : ''}`}
                  data-testid={`tenant-msg-${t.id}`}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-slate-100 flex items-center justify-center font-bold text-slate-600">
                      {t.name?.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <p className="font-medium text-slate-900 text-sm">{t.name}</p>
                      <p className="text-xs text-slate-500">{t.email}</p>
                    </div>
                  </div>
                  {unread[t.id] && (
                    <span className="bg-red-500 text-white text-xs px-2 py-0.5 rounded-full font-bold">{unread[t.id]}</span>
                  )}
                </button>
              ))
            )}
          </div>
        </div>

        {/* Chat Area */}
        <div className="md:col-span-2 bg-white border border-gray-200 flex flex-col">
          {selectedTenant ? (
            <>
              <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
                <h2 className="font-bold text-slate-900">{selectedTenant.name}</h2>
                <p className="text-xs text-slate-500">{selectedTenant.email}</p>
              </div>
              <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="admin-messages-list">
                {messages.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-full text-center text-slate-400">
                    <ChatCircleDots weight="light" className="w-16 h-16 mb-3" />
                    <p>Noch keine Nachrichten</p>
                  </div>
                ) : (
                  messages.map(m => (
                    <div key={m.id} className={`flex ${m.sender_role === 'admin' ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-xs sm:max-w-md ${m.sender_role === 'admin' ? 'bg-slate-900 text-white' : 'bg-emerald-100 text-slate-900'} px-4 py-2.5 rounded-lg`}>
                        <p className="text-xs font-medium opacity-75 mb-1">{m.sender_name}</p>
                        <p className="whitespace-pre-wrap break-words">{m.content}</p>
                        <p className="text-[10px] opacity-60 mt-1 text-right">{formatTime(m.created_at)}</p>
                      </div>
                    </div>
                  ))
                )}
              </div>
              <form onSubmit={handleSend} className="border-t border-gray-200 p-3 flex gap-2">
                <input
                  type="text"
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  placeholder="Antwort an Mieter..."
                  className="flex-1 border border-gray-300 px-4 py-2.5 focus:border-slate-900 outline-none"
                  data-testid="admin-message-input"
                />
                <button
                  type="submit"
                  disabled={sending || !content.trim()}
                  className="bg-slate-900 text-white px-4 py-2.5 hover:bg-slate-800 disabled:opacity-50 flex items-center gap-2"
                  data-testid="admin-message-send-button"
                >
                  <PaperPlaneTilt weight="bold" className="w-4 h-4" />
                  <span className="hidden sm:inline">Senden</span>
                </button>
              </form>
            </>
          ) : (
            <div className="flex-1 flex flex-col items-center justify-center text-slate-400 p-8 text-center">
              <ChatCircleDots weight="light" className="w-20 h-20 mb-3" />
              <p>Wählen Sie einen Mieter aus, um die Konversation zu beginnen.</p>
              <p className="text-xs mt-2">Mieter müssen sich zunächst per Einladungscode im Portal registrieren.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
